import type { JSX, ReactNode } from 'react'
import { BRAND } from '@apiary/shared'
import { Surface } from '@/ui/primitives/Surface'

/**
 * Обёртка экранов входа и регистрации — единственные экраны вне каркаса
 * приложения.
 *
 * Это первое, что видит человек, поэтому здесь чуть больше воздуха и чуть
 * больше «характера», чем внутри панели: знак-соты, медовое свечение за
 * карточкой, крупный заголовок. Но не больше: страница входа, которая
 * рисуется полсекунды, раздражает сильнее, чем радует.
 *
 * Фон — не пустой чёрный: слабый паттерн соты (inline SVG в data-URI, потому что
 * CSP не пускает внешние файлы) плюс одно медовое пятно за карточкой. Оба слоя
 * `aria-hidden` и `pointer-events-none` — они ни во что не участвуют.
 */

const HEX_PATTERN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='56' height='100' viewBox='0 0 56 100'%3E%3Cpath d='M28 0l28 16v32L28 64 0 48V16z' fill='none' stroke='%23F5B93C' stroke-width='1'/%3E%3C/svg%3E\")"

export interface AuthShellProps {
  title: string
  subtitle: string
  children: ReactNode
  /** Строка под карточкой: ссылка на второй экран. */
  footer: ReactNode
}

export function AuthShell({ title, subtitle, children, footer }: AuthShellProps): JSX.Element {
  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden px-4 py-10">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.045]"
        style={{ backgroundImage: HEX_PATTERN }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-1/2 size-[38rem] -translate-x-1/2 -translate-y-1/2 rounded-pill bg-honey/8 blur-[120px]"
      />

      <div className="anim-rise relative flex w-full max-w-[26rem] flex-col gap-6">
        <header className="flex flex-col items-center gap-3 text-center">
          <HexMark />
          <div>
            <p className="font-display text-md font-bold tracking-[0.3em] text-honey">
              {BRAND.name.toUpperCase()}
            </p>
            <p className="mt-1 text-xs text-ink-muted">{BRAND.tagline}</p>
          </div>
        </header>

        <Surface variant="raised" className="p-5 sm:p-6">
          <h1 className="font-display text-xl font-semibold text-ink">{title}</h1>
          <p className="mt-1.5 text-base text-ink-muted">{subtitle}</p>
          <div className="mt-6">{children}</div>
        </Surface>

        <div className="text-center text-xs text-ink-muted">{footer}</div>
      </div>
    </div>
  )
}

/** Знак: сота с медовой кромкой. Один элемент бренда, который здесь уместен. */
function HexMark(): JSX.Element {
  return (
    <span
      aria-hidden
      className="flex size-12 items-center justify-center rounded-md border border-honey/35 bg-honey/10"
      style={{ boxShadow: '0 0 28px -10px var(--color-honey)' }}
    >
      <svg viewBox="0 0 24 24" className="size-6" fill="none" aria-hidden>
        <path
          d="M12 2l8.66 5v10L12 22 3.34 17V7z"
          stroke="var(--color-honey)"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <path
          d="M12 8.5l4.33 2.5v5L12 18.5 7.67 16v-5z"
          fill="var(--color-honey)"
          fillOpacity="0.35"
          stroke="var(--color-honey)"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}
